import React, { useState } from 'react';
import { BadgeCheck, Briefcase, Building, Scale, Calculator, TrendingUp } from 'lucide-react';

// Public signup wizard for partners. Distinct from PartnerOnboardingWizard
// (which is the invite-accept variant for partners who already have an
// account). This one POSTs to /succeedo/partners/register, which creates
// User+Identity+InventoryUser+Preference+CoachProfile atomically and lands
// the user on /succeedo/partner/dashboard.
const PARTNER_TYPES = [
  { value: 'cbv', title: 'Chartered Business Valuator (CBV)', description: 'Professional business valuation services', icon: BadgeCheck },
  { value: 'business_broker', title: 'Business Broker', description: 'Facilitate business sales and acquisitions', icon: Briefcase },
  { value: 'lender', title: 'Commercial Lender', description: 'Provide financing for business transactions', icon: Building },
  { value: 'lawyer', title: 'Business Lawyer', description: 'Legal counsel for business transactions', icon: Scale },
  { value: 'accountant', title: 'Accountant / CPA', description: 'Tax and financial advisory services', icon: Calculator },
  { value: 'cepa_coach', title: 'CEPA-Certified Exit Planner', description: 'Certified exit planning professional', icon: TrendingUp },
];

const PROVINCES = [
  'British Columbia', 'Alberta', 'Saskatchewan', 'Manitoba',
  'Ontario', 'Quebec', 'Atlantic', 'Canada-wide',
];

const INDUSTRIES = [
  'Manufacturing', 'Technology', 'Healthcare', 'Retail',
  'Professional Services', 'Construction', 'Food & Hospitality',
  'Transportation', 'Financial Services', 'Real Estate',
  'Agriculture', 'SaaS', 'E-Commerce',
];

const TOTAL_STEPS = 3;

function getCsrfToken() {
  const meta = document.querySelector('meta[name="csrf-token"]');
  return meta ? meta.getAttribute('content') : '';
}

function toggleInArray(arr, value) {
  return arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];
}

export default function PartnerSignupWizard() {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState([]);

  // Step 1: account
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // Step 2: category + region + industry
  const [partnerCategory, setPartnerCategory] = useState('');
  const [regions, setRegions] = useState([]);
  const [industries, setIndustries] = useState([]);

  // Step 3: bio + opt-in
  const [bio, setBio] = useState('');
  const [acceptsIntroductions, setAcceptsIntroductions] = useState(true);

  const step1Valid = name.trim() && /^.+@.+\..+$/.test(email) && password.length >= 8;
  const step2Valid = partnerCategory && regions.length > 0 && industries.length > 0;
  const step3Valid = bio.trim().length >= 20;

  const handleSubmit = async () => {
    if (submitting) return;
    setErrors([]);
    setSubmitting(true);
    try {
      const res = await fetch('/succeedo/partners/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken(),
          Accept: 'application/json',
        },
        body: JSON.stringify({
          name,
          email,
          password,
          password_confirmation: password,
          partner_category: partnerCategory,
          bio,
          accepts_introductions: acceptsIntroductions,
          geography_regions: regions,
          industry_tags: industries,
        }),
      });
      const data = await res.json();
      if (data.success) {
        if (typeof window.ahoy !== 'undefined') {
          window.ahoy.track('succeedo_partner_signup_completed', { partner_category: partnerCategory });
        }
        window.location.assign(data.redirect_to || '/succeedo/partner/dashboard');
        return;
      }
      setErrors(data.errors || ['Something went wrong. Please try again.']);
    } catch (_e) {
      setErrors(['Network error. Please retry.']);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--page-bg))] flex items-center justify-center p-6">
      <div className="w-full max-w-[640px] bg-card border border-border rounded-[16px] p-8 shadow-sm">
        <div className="mb-6">
          <div className="font-mono text-[11px] uppercase text-muted-foreground/60 tracking-wide mb-2">
            BECOME A SUCCEEDO PARTNER · STEP {step} OF {TOTAL_STEPS}
          </div>
          <h1 className="font-serif font-light text-[28px] text-foreground">
            {step === 1 && 'Create your account'}
            {step === 2 && 'Tell us what you do'}
            {step === 3 && 'A few words about you'}
          </h1>
        </div>

        {errors.length > 0 && (
          <div className="mb-4 rounded-lg bg-destructive/10 border border-destructive/30 text-destructive text-[13px] px-3 py-2">
            {errors.join(', ')}
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-[13px] text-muted-foreground">Your name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Pat Partner"
                className="w-full bg-muted border border-border rounded-[10px] px-3 py-2.5 text-[14px] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--succeedo-orange))]/20"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-[13px] text-muted-foreground">Email address</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="pat@yourfirm.com"
                className="w-full bg-muted border border-border rounded-[10px] px-3 py-2.5 text-[14px] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--succeedo-orange))]/20"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-[13px] text-muted-foreground">Password (8+ characters)</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-muted border border-border rounded-[10px] px-3 py-2.5 text-[14px] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--succeedo-orange))]/20"
              />
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-6">
            <div className="space-y-2">
              <div className="text-[13px] text-muted-foreground">What kind of partner are you?</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {PARTNER_TYPES.map((p) => {
                  const Icon = p.icon;
                  const selected = partnerCategory === p.value;
                  return (
                    <button
                      type="button"
                      key={p.value}
                      onClick={() => setPartnerCategory(p.value)}
                      className={`text-left p-3 rounded-[10px] border-2 transition-colors ${
                        selected
                          ? 'border-[hsl(var(--succeedo-orange))] bg-[hsl(var(--succeedo-orange))]/5'
                          : 'border-border bg-card hover:border-muted-foreground/30'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <Icon className="w-5 h-5 text-[hsl(var(--succeedo-orange))] shrink-0 mt-0.5" />
                        <div>
                          <div className="text-[13px] font-medium">{p.title}</div>
                          <div className="text-[11px] text-muted-foreground mt-0.5">{p.description}</div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="space-y-2">
              <div className="text-[13px] text-muted-foreground">Where do you serve clients? (one or more)</div>
              <div className="flex flex-wrap gap-2">
                {PROVINCES.map((r) => (
                  <button
                    type="button"
                    key={r}
                    onClick={() => setRegions(toggleInArray(regions, r))}
                    className={`text-[12px] rounded-full px-3 py-1 border ${
                      regions.includes(r)
                        ? 'bg-[hsl(var(--succeedo-orange))] text-white border-[hsl(var(--succeedo-orange))]'
                        : 'border-border text-foreground hover:bg-muted'
                    }`}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <div className="text-[13px] text-muted-foreground">Which industries do you focus on? (one or more)</div>
              <div className="flex flex-wrap gap-2">
                {INDUSTRIES.map((i) => (
                  <button
                    type="button"
                    key={i}
                    onClick={() => setIndustries(toggleInArray(industries, i))}
                    className={`text-[12px] rounded-full px-3 py-1 border ${
                      industries.includes(i)
                        ? 'bg-[hsl(var(--succeedo-orange))] text-white border-[hsl(var(--succeedo-orange))]'
                        : 'border-border text-foreground hover:bg-muted'
                    }`}
                  >
                    {i}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-[13px] text-muted-foreground">Short bio (≥ 20 characters)</label>
              <textarea
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                rows={5}
                placeholder="A few sentences about your practice, the kind of clients you work best with, and why a Succeedo seller should pick you."
                className="w-full bg-muted border border-border rounded-[10px] px-3 py-2.5 text-[14px] resize-none focus:outline-none focus:ring-2 focus:ring-[hsl(var(--succeedo-orange))]/20"
              />
              <div className="text-[11px] text-muted-foreground">{bio.trim().length}/20</div>
            </div>
            <label className="flex items-start gap-3 p-3 rounded-[10px] border border-border bg-muted/40 cursor-pointer">
              <input
                type="checkbox"
                checked={acceptsIntroductions}
                onChange={(e) => setAcceptsIntroductions(e.target.checked)}
                className="mt-0.5"
              />
              <div>
                <div className="text-[13px] font-medium">Accept introduction requests</div>
                <div className="text-[12px] text-muted-foreground">
                  When sellers see your profile, they can request an introduction. You'll get an email and you can accept or decline each one.
                </div>
              </div>
            </label>
          </div>
        )}

        <div className="flex items-center justify-between gap-3 pt-6 mt-6 border-t border-border">
          {step > 1 ? (
            <button
              type="button"
              onClick={() => setStep((s) => s - 1)}
              disabled={submitting}
              className="border border-border text-foreground rounded-full px-5 py-2 text-[13px] hover:bg-muted disabled:opacity-50"
            >
              Back
            </button>
          ) : (
            <span />
          )}
          {step < TOTAL_STEPS ? (
            <button
              type="button"
              onClick={() => setStep((s) => s + 1)}
              disabled={(step === 1 && !step1Valid) || (step === 2 && !step2Valid)}
              className="bg-[hsl(var(--succeedo-orange))] text-white rounded-full px-5 py-2 text-[13px] hover:opacity-90 disabled:opacity-40"
            >
              Continue
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!step3Valid || submitting}
              className="bg-[hsl(var(--succeedo-orange))] text-white rounded-full px-5 py-2 text-[13px] hover:opacity-90 disabled:opacity-40"
            >
              {submitting ? 'Creating account…' : 'Create my partner account'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
